import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, Lock, CheckCircle2, ShieldCheck, ShieldAlert, Languages,
  Mail, Award, BarChart3, Users, ClipboardList, Zap, BookOpen, FileSpreadsheet,
  Gauge, Globe
} from 'lucide-react';
import loginImg from '../assets/case-training/login.jpg';
import homeImg from '../assets/case-training/home.jpg';
import objectivesImg from '../assets/case-training/objectives.jpg';
import certificateImg from '../assets/case-training/certificate.jpg';
import adminImg from '../assets/case-training/admin.jpg';

const CaseStudyTraining = () => {
  const problem = [
    'Manual processes — PDF documents, email chains and in-person sessions.',
    'No tracking — HR had no visibility into who completed training, when, or how they scored.',
    'No accountability — due dates, reminders and certificate expiry tracked via spreadsheets, or not at all.',
    'Compliance gaps — ISO 27001 requires demonstrable, auditable evidence that all staff receive and acknowledge security-awareness training.',
  ];

  const capabilities = [
    { icon: <BookOpen size={18} />, title: 'Multi-course training management', text: 'Information & Cyber Security, GDPR & Data Protection, Phishing & Social Engineering, and custom courses.' },
    { icon: <ClipboardList size={18} />, title: 'Interactive module-based learning', text: 'Scenarios, quizzes, checklists, phishing email examples and a password-strength checker.' },
    { icon: <Languages size={18} />, title: 'Bilingual (English / Ukrainian)', text: 'Full content parity across languages, switchable per user.' },
    { icon: <Zap size={18} />, title: 'Automated assignment & due dates', text: '2-week deadlines with overdue tracking.' },
    { icon: <Gauge size={18} />, title: 'Final quiz with scoring', text: 'Configurable passing threshold (default 70%).' },
    { icon: <Award size={18} />, title: 'Certificate generation', text: 'Professional PDF certificates with 1-year validity.' },
    { icon: <BarChart3 size={18} />, title: 'Admin dashboard', text: 'User management, bulk CSV import, training analytics, quiz editor and reminder system.' },
    { icon: <Mail size={18} />, title: 'Email notifications', text: 'Automated welcome emails, assignment notices, due-date reminders and certificate-renewal alerts.' },
    { icon: <FileSpreadsheet size={18} />, title: 'ISO-ready CSV reporting', text: 'Exportable training-completion data for audit evidence.' },
  ];

  const steps = [
    'Admin imports employees via CSV (name, email, department, role, location, manager).',
    'Admin assigns training courses — each employee receives an email with a direct link.',
    'Employee logs in, sees their assigned trainings, and works through modules at their own pace.',
    'Each module contains structured sections (text, warnings, checklists, interactive scenarios, real phishing examples).',
    'Progress is tracked in real time — admins see per-user completion percentages.',
    'After completing all modules, the employee takes a final quiz.',
    'Upon passing, a certificate is generated with a 1-year expiry date.',
    'Automated reminders notify employees of upcoming deadlines and certificate renewals.',
    'Admin exports ISO reports — CSV with all users, completion status, scores and dates for audit evidence.',
  ];

  const stack = [
    ['Frontend', 'React, Tailwind CSS, React Router, Lucide Icons'],
    ['Backend', 'Python, FastAPI, Pydantic'],
    ['Database', 'MongoDB Atlas'],
    ['Email', 'SendGrid API (domain-authenticated transactional emails)'],
    ['Auth', 'JWT-based authentication with role-based access (admin/employee)'],
    ['Deployment', 'Render (backend), Netlify (frontend)'],
    ['Certificates', 'Server-side PDF generation'],
  ];

  const screens = [
    { title: 'Secure Login', caption: 'Sign-in to the training portal (bilingual EN/UA).', image: loginImg },
    { title: 'Training Home', caption: 'Available courses — Information & Cyber Security, GDPR and Phishing — with a language switch.', image: homeImg },
    { title: 'Course Objectives & Progress', caption: 'Learning objectives with live progress: modules completed and time spent.', image: objectivesImg },
    { title: 'Completion Certificate', caption: 'Auto-generated PDF certificate on passing, with 1-year validity.', image: certificateImg },
    { title: 'Admin Dashboard', caption: 'Completion rate, average time, certificates issued and per-user progress. Personal data blurred.', image: adminImg },
  ];

  const benefitGroups = [
    {
      icon: <ShieldCheck className="text-brand-primary" size={20} />, title: 'Compliance & audit',
      items: [
        'Auditable proof of employee training completion for ISO 27001 certification.',
        'Exportable reports satisfy auditor requirements for competence and awareness evidence.',
        'Certificate-validity tracking ensures continuous compliance.',
      ],
    },
    {
      icon: <Zap className="text-brand-primary" size={20} />, title: 'Operational efficiency',
      items: [
        'Eliminated manual training coordination previously run through emails and spreadsheets.',
        'Automated reminders reduced HR follow-up workload.',
        'Bulk user import and assignment saves hours of administrative work.',
      ],
    },
    {
      icon: <ShieldAlert className="text-brand-primary" size={20} />, title: 'Risk reduction',
      items: [
        'Employees are trained on real-world threats (phishing examples, social-engineering tactics).',
        'Interactive scenarios reinforce learning beyond passive reading.',
        'Regular certificate renewal keeps knowledge current.',
      ],
    },
    {
      icon: <Globe className="text-brand-primary" size={20} />, title: 'Scalability & localisation',
      items: [
        'Ukrainian language support serves the full workforce without parallel materials.',
        'Admins add new courses and modules without developer involvement.',
        'Quiz editor allows content updates as threats evolve.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-bg-page" data-testid="case-study-training">
      {/* Simple top bar */}
      <header className="sticky top-0 z-50 bg-bg-page/90 backdrop-blur-md border-b border-border-medium">
        <div className="container mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 nav-link" data-testid="case-back-link">
            <ArrowLeft size={18} />
            Back to portfolio
          </Link>
          <a
            href="/Anna-Krat-CV.pdf"
            download="Anna-Krat-CV.pdf"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-primary text-white text-sm font-medium hover:bg-brand-hover transition-colors"
            data-testid="case-download-cv-btn"
          >
            <Download size={16} />
            CV
          </a>
        </div>
      </header>

      <main className="container mx-auto px-8 py-16 max-w-5xl">
        {/* Title */}
        <div className="mb-12">
          <div className="flex items-center flex-wrap gap-3 mb-5">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-brand-soft text-brand-primary border border-indigo-100">
              Full-stack • Live • Private company system
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
              <Lock size={13} /> Contains personal data — public login not available
            </span>
          </div>
          <h1 className="heading-1 text-text-primary mb-3">Corporate Training & Compliance Platform</h1>
          <p className="caption text-brand-primary mb-5">Pocketbook International SA</p>
          <p className="body-large text-text-secondary max-w-3xl">
            A full-stack corporate training platform built specifically for Pocketbook&apos;s ISO 27001 compliance needs.
            It digitises the entire training lifecycle — from assignment to certification — with full audit trails.
          </p>

          {/* Quick stats */}
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            {[
              { icon: <ShieldCheck className="text-brand-primary" size={22} />, big: 'ISO 27001 audit-ready', small: 'Auditable proof of training completion' },
              { icon: <Languages className="text-brand-primary" size={22} />, big: 'Bilingual EN / UA', small: 'Full content parity, switchable per user' },
              { icon: <Award className="text-brand-primary" size={22} />, big: 'Full lifecycle', small: 'Assignment → learning → quiz → certificate' },
            ].map((s, i) => (
              <div key={i} className="team-card p-6" data-testid={`case-stat-${i}`}>
                <div className="mb-3">{s.icon}</div>
                <div className="heading-5 text-text-primary mb-1">{s.big}</div>
                <div className="body-small text-text-secondary">{s.small}</div>
              </div>
            ))}
          </div>
        </div>

        {/* My role */}
        <section className="team-card p-8 mb-16">
          <h2 className="heading-4 text-text-primary mb-3">My role</h2>
          <p className="body-medium">
            Delivered end-to-end, from idea to implementation — workflow design, UX/UI, requirements, full-stack build,
            transactional email (SendGrid), server-side PDF certificates, testing and deployment — built by me with AI agents,
            as part of the ISO 27001 implementation at Pocketbook International SA.
          </p>
        </section>

        {/* Background & problem */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-4">Background & problem</h2>
          <p className="body-medium mb-8 max-w-3xl">
            Before this platform there was no centralised training system. Onboarding and compliance training relied on manual,
            untrackable processes — a direct risk to ISO 27001 evidence of competence and awareness.
          </p>
          <ul className="grid md:grid-cols-2 gap-4">
            {problem.map((p, i) => (
              <li key={i} className="team-card p-5 flex items-start gap-3">
                <div className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0 mt-2" />
                <span className="body-small text-text-primary">{p}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* The solution - capabilities */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-3">The solution</h2>
          <p className="body-medium mb-8 max-w-3xl">Key capabilities across the training lifecycle:</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c, i) => (
              <div key={i} className="team-card p-6" data-testid={`case-capability-${i}`}>
                <div className="flex items-center gap-2 mb-2 text-brand-primary">
                  {c.icon}
                  <h3 className="heading-6 text-text-primary">{c.title}</h3>
                </div>
                <p className="body-small text-text-secondary">{c.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Inside the platform gallery */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-3">Inside the platform</h2>
          <p className="body-medium mb-8 max-w-3xl">A look at the live application. Personal data (names and emails) has been blurred for this case study.</p>
          <div className="grid md:grid-cols-2 gap-8">
            {screens.map((screen, i) => (
              <div key={i} className="team-card overflow-hidden" data-testid={`case-screen-${i}`}>
                <a
                  href={screen.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-[16/10] bg-bg-light border-b border-border-medium flex items-center justify-center overflow-hidden group"
                >
                  <img
                    src={screen.image}
                    alt={screen.title}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </a>
                <div className="p-6">
                  <h3 className="heading-6 text-text-primary mb-2">{i + 1}. {screen.title}</h3>
                  <p className="body-small text-text-secondary">{screen.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">How it works</h2>
          <ol className="space-y-3">
            {steps.map((step, i) => (
              <li key={i} className="team-card p-5 flex items-start gap-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-soft text-brand-primary text-sm font-semibold flex items-center justify-center">{i + 1}</span>
                <span className="body-small text-text-primary pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Tech stack */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Technology stack & APIs</h2>
          <div className="team-card overflow-hidden">
            {stack.map(([k, v], i) => (
              <div key={i} className={`grid grid-cols-1 sm:grid-cols-3 gap-2 px-6 py-4 ${i !== stack.length - 1 ? 'border-b border-border-light' : ''}`}>
                <span className="caption text-brand-primary">{k}</span>
                <span className="body-small text-text-primary sm:col-span-2">{v}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-16">
          <h2 className="heading-3 text-text-primary mb-8">Benefits to the company</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {benefitGroups.map((g, i) => (
              <div key={i} className="team-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  {g.icon}
                  <h3 className="heading-6 text-text-primary">{g.title}</h3>
                </div>
                <ul className="space-y-2">
                  {g.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle2 size={15} className="text-brand-primary flex-shrink-0 mt-1" />
                      <span className="body-small text-text-secondary">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Closing note */}
        <div className="team-card p-6 mb-16">
          <p className="body-small text-text-secondary italic">
            Built as part of ISO 27001 implementation at Pocketbook International SA, transforming compliance training from a
            manual, untrackable process into a fully digital, auditable platform.
          </p>
        </div>

        {/* Back CTA */}
        <div className="text-center">
          <Link to="/" className="btn-secondary inline-flex items-center gap-2" data-testid="case-back-bottom">
            <ArrowLeft size={18} />
            Back to portfolio
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CaseStudyTraining;
